import React, { createContext, useCallback, useEffect, useState } from 'react';
import { PositionType, RGBA, Subscription, User } from '@/common/constants/interfaces';
import { request } from '@/common/utils/utils';
import URLS from '../constants/url';
import localStorageUtil from '../utils/localStorageUtil';
import { getDefaultFontName } from '@/common/constants/font';
import { createWatermark } from '@/common/utils/watermarkUtil';
import userAgentUtils from '@/common/utils/userAgent';
import { Watermark } from 'antd';
import { FileInfo } from '@/common/services/FileCenter';
import posthog from 'posthog-js';

export interface WH {
    width: number;
    height: number;
}

// 编辑视图状态，目前只有mobile用到
export enum EditViewStatus {
    Default, // 未选中任何水印 ，默认状态，点击后进入Selected状态
    Selected, // 选中了某个水印，可以对水印进行移动和编辑
    TextEditing, // 正在编辑文字
}

export interface Position {
    positionType: PositionType;
    offsetX?: number;
    offsetY?: number;
    spaceX?: number; // 水平间隔刷了
    spaceY?: number; // 垂直间隔
}

export interface StrokeProps {
    strokeColor: RGBA;
    strokeWidth: number;
}

export interface Background {
    color: RGBA;
    radius: number;
}

export interface ShadowBlur {
    shadowColor: string;
    blur: number;
}

export interface Shadow {
    shadowColor: RGBA;
    alpha?: number;
    offsetX: number;
    offsetY: number;
    blur?: number;
}

export interface Watermark {
    id: string;
    waterMarkType: string;
    waterMarkTemplate: string;
    fontName: string;
    fontSize: number;
    fontColor: RGBA;
    isBold: boolean;
    isItalic: boolean;
    isUnderline: boolean;
    isStrikethrough: boolean;
    stroke: StrokeProps | null;
    background: Background | null;
    shadowBlur: ShadowBlur | null;
    shadow: Shadow | null;
    angel: number;
    tran: number;
    position: Position;
}

export interface IFileCenterContext {
    userInfo: User | null;
    setUserInfo: (User: any) => void;
    subscription: Subscription | null;
    setSubscription: (subscription: Subscription | null) => void;
    fileIdList: string[];
    setFileIdList: (fileIdList: string[]) => void;
    selectedFileInfo: FileInfo | undefined;
    setSelectedFileInfo: (selectedFileInfo: FileInfo | undefined) => void;

    // 水印设置
    watermarks: Watermark[];
    addWatermark: (watermark: Watermark) => void;
    updateWatermark: (id: string, updatedWatermark: Partial<Watermark>) => void;
    updateSelectedWatermark: (updatedWatermark: Partial<Watermark>) => void;
    removeWatermark: (id: string) => void;
    getSelectedWatermark: () => Watermark | undefined;
    selectedWatermarkId: string | null;
    setSelectedWatermarkId: (id: string | null) => void;

    //水印类型
    waterMarkType: string;
    setWaterMarkType: (waterMarkType: string) => void;
    //水印内容
    waterMarkTemplate: string,
    setWaterMarkTemplate: (waterMarkTemplate: string) => void;
    fontName: string,
    setFontName: (fontName: string) => void;
    // 在原始图片上的字体大小
    fontSize: number,
    setFontSize: (fontSize: number) => void;
    fontColor: RGBA,
    setFontColor: (fontColor: RGBA) => void;
    isBold: boolean,
    setIsBold: (isBold: boolean) => void,
    isItalic: boolean,
    setIsItalic: (isItalic: boolean) => void,
    isUnderline: boolean,
    setIsUnderline: (isUnderline: boolean) => void,
    isStrikethrough: boolean,
    setIsStrikethrough: (isStrikethrough: boolean) => void,
    stroke: StrokeProps | null,
    setStroke: (stroke: StrokeProps | null) => void;
    background: Background | null;
    setBackground: (background: Background | null) => void;
    shadowBlur: ShadowBlur | null;
    setShadowBlur: (shadowBlur: ShadowBlur | null) => void;
    shadow: Shadow | null;
    setShadow: (shadow: Shadow | null) => void;
    //方向
    angel: number,
    setAngel: (angel: number) => void;
    //透明度
    tran: number,
    setTran: (tran: number) => void;

    // 水印位置
    position: Position,
    setPosition: (position: Position) => void;
    // 预览页面图片容器的宽高
    imageEditViewWH: WH,
    setImageEditViewWH: (imageEditViewWH: WH) => void;
    imageEditViewStatus: EditViewStatus;
    setImageEditViewStatus: (imageEditViewStatus: EditViewStatus) => void;
}

export const FileCenterContext = createContext<IFileCenterContext>({
    userInfo: null,
    setUserInfo: (userInfo: any) => { },
    subscription: null,
    setSubscription: (subscription: Subscription | null) => { },
    fileIdList: [],
    setFileIdList: (fileIdList: string[]) => { },
    selectedFileInfo: undefined,
    setSelectedFileInfo: (selectedFileInfo: FileInfo | undefined) => { },

    //水印类型
    watermarks: [],
    addWatermark: (watermark: Watermark) => { },
    updateWatermark: (id: string, updatedWatermark: Partial<Watermark>) => { },
    updateSelectedWatermark: (updatedWatermark: Partial<Watermark>) => { },
    removeWatermark: (id: string) => { },
    getSelectedWatermark: () => { return undefined },

    selectedWatermarkId: null,
    setSelectedWatermarkId: (id: string | null) => { },

    waterMarkType: 'text',
    setWaterMarkType: (waterMarkType: string) => { },
    //水印内容模板
    waterMarkTemplate: '',
    setWaterMarkTemplate: (waterMarkTemplate: string) => { },
    fontName: getDefaultFontName(),
    setFontName: (fontName: string) => { },
    // 在原始图片上的字体大小
    fontSize: 48,
    setFontSize: (fontSize: number) => { },
    fontColor: RGBA.fromHex('#1677ff'),
    setFontColor: (fontColor: RGBA) => { },
    isBold: false,
    setIsBold: (isBold: boolean) => { },
    isItalic: false,
    setIsItalic: (isItalic: boolean) => { },
    isUnderline: false,
    setIsUnderline: (isUnderline: boolean) => { },
    isStrikethrough: false,
    setIsStrikethrough: (isStrikethrough: boolean) => { },
    stroke: null,
    setStroke: (stroke: StrokeProps | null) => { },
    background: null,
    setBackground: (background: Background | null) => { },
    shadowBlur: null,
    setShadowBlur: (shadowBlur: ShadowBlur | null) => { },
    shadow: null,
    setShadow: (shadow: Shadow | null) => { },
    //方向
    angel: 0,
    setAngel: (angel: number) => { },
    //透明度
    tran: 0,
    setTran: (tran: number) => { },

    position: { positionType: PositionType.RightBottom, offsetX: 8, offsetY: 5, spaceX: 10, spaceY: 10 },
    setPosition: (position: Position) => { },
    imageEditViewWH: { width: 0, height: 0 },
    setImageEditViewWH: (imageEditViewWH: WH) => { },
    imageEditViewStatus: EditViewStatus.Default,
    setImageEditViewStatus: (imageEditViewStatus: EditViewStatus) => { },
})

interface IProps {
    children: React.ReactNode;
}

const token = localStorageUtil.getItem('token');
const localUserInfo = localStorageUtil.getItem('userInfo');

// const initialWatermarks: Watermark[] = userAgentUtils.getDeviceType() === 'mobile' ? [] : [createWatermark()];
const initialWatermark = createWatermark();
const initialSelectedWatermarkId = userAgentUtils.getDeviceType() === 'mobile' ? null : initialWatermark.id;

const FileCenterProvider = (props: IProps) => {
    // 为了避免刷新页面，用户avatar图标闪烁，这里先使用localStorage存储的用户信息
    const [userInfo, setUserInfo] = useState<User | null>(token ? localUserInfo : null);
    const [subscription, setSubscription] = useState<Subscription | null>(null);

    const [fileIdList, setFileIdList] = useState<any>([]);
    const [selectedFileInfo, setSelectedFileInfo] = useState<FileInfo | undefined>(undefined)

    const [imageEditViewWH, setImageEditViewWH] = useState<WH>({ width: 0, height: 0 });
    const [imageEditViewStatus, setImageEditViewStatus] = useState<EditViewStatus>(EditViewStatus.Default);

    const [watermarks, setWatermarks] = useState<Watermark[]>([initialWatermark]);
    const [selectedWatermarkId, setSelectedWatermarkId] = useState<string | null>(initialSelectedWatermarkId);

    const selectedWatermark = watermarks.find(wm => wm.id === selectedWatermarkId);

    const addWatermark = useCallback((watermark: Watermark) => {
        setWatermarks([...watermarks, watermark]);
    }, [watermarks, setWatermarks]);

    const updateWatermark = useCallback((id: string, updatedWatermark: Partial<Watermark>) => {
        const newWatermarks = watermarks.map(wm => wm.id === id ? { ...wm, ...updatedWatermark } : wm);
        setWatermarks(newWatermarks);
    }, [watermarks, setWatermarks]);

    const updateSelectedWatermark = useCallback((updatedWatermark: Partial<Watermark>) => {
        if (!selectedWatermarkId) {
            return;
        }
        updateWatermark(selectedWatermarkId, updatedWatermark);
    }, [selectedWatermarkId, updateWatermark]);

    const removeWatermark = useCallback((id: string) => {
        setWatermarks(watermarks.filter(wm => wm.id !== id));
    }, [watermarks, setWatermarks]);

    const getSelectedWatermark = useCallback(() => {
        return watermarks.find(wm => wm.id === selectedWatermarkId);
    }, [watermarks, selectedWatermarkId]);

    const setWaterMarkType = useCallback((waterMarkType: string) => {
        if (!selectedWatermarkId) {
            return;
        }
        updateWatermark(selectedWatermarkId, { waterMarkType });
    }, [selectedWatermarkId, updateWatermark]);

    const setWaterMarkTemplate = useCallback((waterMarkTemplate: string) => {
        if (!selectedWatermarkId) {
            return;
        }
        updateWatermark(selectedWatermarkId, { waterMarkTemplate });
    }, [selectedWatermarkId, updateWatermark]);

    const setFontName = useCallback((fontName: string) => {
        if (!selectedWatermarkId) {
            return;
        }
        updateWatermark(selectedWatermarkId, { fontName });
    }, [selectedWatermarkId, updateWatermark]);

    const setFontSize = useCallback((fontSize: number) => {
        if (!selectedWatermarkId) {
            return;
        }
        updateWatermark(selectedWatermarkId, { fontSize });
    }, [selectedWatermarkId, updateWatermark]);

    const setFontColor = useCallback((fontColor: RGBA) => {
        if (!selectedWatermarkId) {
            return;
        }
        updateWatermark(selectedWatermarkId, { fontColor });
    }, [selectedWatermarkId, updateWatermark]);

    const setIsBold = useCallback((isBold: boolean) => {
        if (!selectedWatermarkId) {
            return;
        }
        updateWatermark(selectedWatermarkId, { isBold });
    }, [selectedWatermarkId, updateWatermark]);

    const setIsItalic = useCallback((isItalic: boolean) => {
        if (!selectedWatermarkId) {
            return;
        }
        updateWatermark(selectedWatermarkId, { isItalic });
    }, [selectedWatermarkId, updateWatermark]);

    const setIsUnderline = useCallback((isUnderline: boolean) => {
        if (!selectedWatermarkId) {
            return;
        }
        updateWatermark(selectedWatermarkId, { isUnderline });
    }, [selectedWatermarkId, updateWatermark]);

    const setIsStrikethrough = useCallback((isStrikethrough: boolean) => {
        if (!selectedWatermarkId) {
            return;
        }
        updateWatermark(selectedWatermarkId, { isStrikethrough });
    }, [selectedWatermarkId, updateWatermark]);

    const setStroke = useCallback((stroke: StrokeProps | null) => {
        if (!selectedWatermarkId) {
            return;
        }
        updateWatermark(selectedWatermarkId, { stroke });
    }, [selectedWatermarkId, updateWatermark]);

    const setBackground = useCallback((background: Background | null) => {
        if (!selectedWatermarkId) {
            return;
        }
        updateWatermark(selectedWatermarkId, { background });
    }, [selectedWatermarkId, updateWatermark]);

    const setShadowBlur = useCallback((shadowBlur: ShadowBlur | null) => {
        if (!selectedWatermarkId) {
            return;
        }
        updateWatermark(selectedWatermarkId, { shadowBlur });
    }, [selectedWatermarkId, updateWatermark]);

    const setShadow = useCallback((shadow: Shadow | null) => {
        if (!selectedWatermarkId) {
            return;
        }
        updateWatermark(selectedWatermarkId, { shadow });
    }, [selectedWatermarkId, updateWatermark]);

    const setAngel = useCallback((angel: number) => {
        if (!selectedWatermarkId) {
            return;
        }
        updateWatermark(selectedWatermarkId, { angel });
    }, [selectedWatermarkId, updateWatermark]);

    const setTran = useCallback((tran: number) => {
        if (!selectedWatermarkId) {
            return;
        }
        updateWatermark(selectedWatermarkId, { tran });
    }, [selectedWatermarkId, updateWatermark]);

    const setPosition = useCallback((position: Position) => {
        if (!selectedWatermarkId) {
            return;
        }
        updateWatermark(selectedWatermarkId, { position });
    }, [selectedWatermarkId, updateWatermark]);



    const {
        waterMarkType,
        waterMarkTemplate,
        fontName,
        fontSize,
        fontColor,
        isBold,
        isItalic,
        isUnderline,
        isStrikethrough,
        stroke,
        background,
        shadowBlur,
        shadow,
        angel,
        tran,
        position,
    } = selectedWatermark || initialWatermark;

    useEffect(() => {
        const fetchUserInfo = async () => {
            if (!token) { // 还未登录
                return
            }

            try {
                // 获取用户信息
                const data = await request<User>({ url: URLS.GET_USERINFO_PATH, method: 'GET' })
                setUserInfo(data);
                localStorageUtil.setItem('userInfo', data);
                posthog.identify(
                    data.id + '',
                    data,
                );

            } catch (error) {
                // 获取用户信息失败，可能是token过期
                // 清除登录信息
                localStorage.removeItem('token');
                localStorage.removeItem('userInfo');
                setUserInfo(null);
            }
        };

        fetchUserInfo();
    }, [])

    useEffect(() => {
        const fetchSubscription = async () => {
            if (!userInfo) {
                return
            }
            const data = await request<Subscription>({ url: URLS.GET_LATEST_SUBSCRIPTION_PATH, method: 'GET' })
            setSubscription(data)
        }

        fetchSubscription();
    }, [userInfo])

    return (
        <FileCenterContext.Provider value={{
            userInfo,
            setUserInfo,
            subscription,
            setSubscription,
            fileIdList,
            setFileIdList,
            selectedFileInfo,
            setSelectedFileInfo,

            imageEditViewWH,
            setImageEditViewWH,
            imageEditViewStatus,
            setImageEditViewStatus,

            // 多个水印设置
            watermarks,
            addWatermark,
            updateWatermark,
            updateSelectedWatermark,
            removeWatermark,
            getSelectedWatermark,

            selectedWatermarkId,
            setSelectedWatermarkId,

            // 选中的水印设置
            waterMarkType,
            setWaterMarkType,
            waterMarkTemplate,
            setWaterMarkTemplate,
            fontName,
            setFontName,
            fontSize,
            setFontSize,
            fontColor,
            setFontColor,
            isBold,
            setIsBold,
            isItalic,
            setIsItalic,
            isUnderline,
            setIsUnderline,
            isStrikethrough,
            setIsStrikethrough,
            stroke,
            setStroke,
            background,
            setBackground,
            shadowBlur,
            setShadowBlur,
            shadow,
            setShadow,
            angel,
            setAngel,
            tran,
            setTran,
            position,
            setPosition,
        }}>
            {props.children}
        </FileCenterContext.Provider>
    );
};

export default FileCenterProvider;
